import type { ContentCardsLayoutTemplate, ContentCardsSettings } from './types';
import { DEFAULT_CONTENT_CARDS_SETTINGS } from './types';

export interface ContentCardLayoutPreset {
  id: ContentCardsLayoutTemplate;
  label: string;
  description: string;
  /** Desktop sütun sayısı önerisi (grid / custom / featured için) */
  columnsDesktop: number;
  /** Sidebar düzenlerinde sağ/sol ızgara sütun sayısı */
  sideGridColumns: number;
}

export const CONTENT_CARD_LAYOUT_TEMPLATES: ContentCardLayoutPreset[] = [
  {
    id: 'grid',
    label: 'Düz Izgara',
    description: 'Kartlar eşit sütunlarda yan yana dizilir',
    columnsDesktop: 3,
    sideGridColumns: 2
  },
  {
    id: 'promo-trio',
    label: '3 Yan Yana Promo',
    description: 'Ana sayfa için 3 eşit promo kartı (resim üstüne yazı için Overlay stilini seçin)',
    columnsDesktop: 3,
    sideGridColumns: 3
  },
  {
    id: 'promo-large-left',
    label: 'Büyük Sol + 2 Küçük',
    description: 'Solda geniş kart, sağda iki küçük kart üst üste (moda vitrin düzeni)',
    columnsDesktop: 2,
    sideGridColumns: 1
  },
  {
    id: 'promo-large-right',
    label: '2 Küçük + Büyük Sağ',
    description: 'Solda iki küçük kart, sağda geniş kart',
    columnsDesktop: 2,
    sideGridColumns: 1
  },
  {
    id: 'sidebar-left',
    label: 'Sol Panel + Izgara',
    description: 'İlk kart solda uzun panel, diğerleri sağda ızgara (örnek: özellikler + ürünler)',
    columnsDesktop: 3,
    sideGridColumns: 2
  },
  {
    id: 'sidebar-right',
    label: 'Izgara + Sağ Panel',
    description: 'Diğer kartlar solda ızgara, ilk kart sağda uzun panel',
    columnsDesktop: 3,
    sideGridColumns: 2
  },
  {
    id: 'featured',
    label: 'Öne Çıkan + Izgara',
    description: 'İlk kart üstte tam genişlik, kalanlar altta ızgara',
    columnsDesktop: 3,
    sideGridColumns: 3
  },
  {
    id: 'custom',
    label: 'Özel Sütun',
    description: 'Her karta istediğiniz kadar sütun genişliği verin (1–4)',
    columnsDesktop: 4,
    sideGridColumns: 2
  }
];

export const MAX_CONTENT_CARDS = 12;

export const normalizeContentCardsLayout = (value: unknown): ContentCardsLayoutTemplate => {
  const raw = String(value || '').trim();
  if (
    raw === 'grid' ||
    raw === 'sidebar-left' ||
    raw === 'sidebar-right' ||
    raw === 'featured' ||
    raw === 'custom' ||
    raw === 'promo-trio' ||
    raw === 'promo-large-left' ||
    raw === 'promo-large-right'
  ) {
    return raw;
  }
  return 'grid';
};

export const getContentCardLayoutTemplate = (
  id: ContentCardsLayoutTemplate
): ContentCardLayoutPreset =>
  CONTENT_CARD_LAYOUT_TEMPLATES.find((item) => item.id === id) || CONTENT_CARD_LAYOUT_TEMPLATES[0];

export const getContentCardLayoutLabel = (id: ContentCardsLayoutTemplate): string =>
  getContentCardLayoutTemplate(id).label;

export const applyLayoutTemplateToSettings = (
  layoutTemplate: ContentCardsLayoutTemplate,
  current?: Partial<ContentCardsSettings>
): ContentCardsSettings => {
  const preset = getContentCardLayoutTemplate(layoutTemplate);
  const isSidebar = layoutTemplate === 'sidebar-left' || layoutTemplate === 'sidebar-right';
  const isPromoAsymmetric =
    layoutTemplate === 'promo-large-left' || layoutTemplate === 'promo-large-right';
  const columnsDesktop = isSidebar
    ? preset.sideGridColumns
    : layoutTemplate === 'promo-trio'
      ? 3
      : preset.columnsDesktop;

  const next: ContentCardsSettings = {
    ...DEFAULT_CONTENT_CARDS_SETTINGS,
    ...current,
    layoutTemplate,
    columnsDesktop,
    columnsTablet: Math.min(columnsDesktop, isPromoAsymmetric ? 1 : 2)
  };

  // Promo şablonlarında varsayılan olarak sade banner + yumuşak köşe
  if (layoutTemplate === 'promo-trio' || isPromoAsymmetric) {
    if (!current?.variant || current.variant === 'minimal') {
      next.variant = 'banner';
    }
    next.showBullets = false;
    next.showDate = true;
    next.cardRadiusPx = current?.cardRadiusPx ?? 20;
    next.gapPx = current?.gapPx ?? 12;
    next.cardTitleStyles = {
      fontFamily: current?.cardTitleStyles?.fontFamily || 'system-ui, -apple-system, "Segoe UI", sans-serif',
      fontSize: current?.cardTitleStyles?.fontSize || 16,
      color: current?.cardTitleStyles?.color || '#ffffff',
      bold: current?.cardTitleStyles?.bold !== false,
      italic: current?.cardTitleStyles?.italic || false,
    };
  }

  return next;
};
